import React, { useState, useEffect, ChangeEvent } from 'react';
import styled from 'styled-components';
import { Mail, Send, Save, TestTube, X, Plus } from 'lucide-react';
import apiService from '../services/api';
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  CardBody,
  Alert,
  Badge,
  Button,
  LoadingState,
  FormGroup,
  Label,
  Input,
} from '../components/ui';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--space-xl);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
`;

const ReadOnlyBox = styled.div`
  padding: var(--space-sm) var(--space-md);
  background: var(--gray-50);
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  color: var(--text-primary);
`;

const HelperText = styled.div`
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  margin-top: var(--space-xs);
`;

const InfoText = styled.p`
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin: 0 0 var(--space-md);
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--gray-200);
  margin: var(--space-lg) 0;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin-bottom: var(--space-lg);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--gray-700);
  cursor: pointer;
`;

const DestinatariContainer = styled.div`
  margin: var(--space-md) 0;
`;

const DestinatariList = styled.ul`
  list-style: none;
  padding: 0;
  margin: var(--space-sm) 0 0;
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-md);
  background: var(--gray-50);
  max-height: 200px;
  overflow-y: auto;
`;

const DestinatarioItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  border-bottom: 1px solid var(--gray-100);
  font-size: var(--font-size-sm);

  &:last-child {
    border-bottom: none;
  }
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: var(--space-xs);
  border-radius: var(--border-radius-sm);
  color: var(--gray-500);
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);

  &:hover {
    background: var(--error-soft);
    color: var(--error);
  }
`;

const AddRow = styled.div`
  display: flex;
  gap: var(--space-sm);
  margin-top: var(--space-sm);

  input {
    flex: 1;
  }
`;

interface EmailConfig {
  brevo_api_key: string;
  email_mittente: string;
  nome_mittente: string;
  destinatari_notifiche: string;
  notifica_produzione: boolean;
  abilitato: boolean;
}

interface EmailUserConfig {
  destinatari_notifiche: string;
  notifica_produzione: boolean;
  abilitato: boolean;
}

const ImpostazioniEmailPage: React.FC = () => {
  const [config, setConfig] = useState<EmailConfig>({
    brevo_api_key: '',
    email_mittente: '',
    nome_mittente: '',
    destinatari_notifiche: '[]',
    notifica_produzione: true,
    abilitato: true
  });

  const [userConfig, setUserConfig] = useState<EmailUserConfig>({
    destinatari_notifiche: '[]',
    notifica_produzione: true,
    abilitato: true
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [newDestinatario, setNewDestinatario] = useState('');
  const [alert, setAlert] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success'
  });

  useEffect(() => {
    caricaConfigurazione();
  }, []);

  const caricaConfigurazione = async () => {
    try {
      setLoading(true);
      const data = await apiService.getEmailConfig();

      // Trasforma l'array di configurazioni in un oggetto
      const configObj: EmailConfig = {
        brevo_api_key: '',
        email_mittente: '',
        nome_mittente: '',
        destinatari_notifiche: '[]',
        notifica_produzione: true,
        abilitato: true
      };

      // Gestisce sia il formato array che oggetto con configurations
      const configurations = Array.isArray(data) ? data : data.configurations || [];

      configurations.forEach((item: any) => {
        if (item.chiave === 'BREVO_API_KEY') configObj.brevo_api_key = item.valore || '';
        if (item.chiave === 'email_mittente') configObj.email_mittente = item.valore || '';
        if (item.chiave === 'email_nome_mittente') configObj.nome_mittente = item.valore || '';
        if (item.chiave === 'email_destinatari_notifiche') configObj.destinatari_notifiche = item.valore || '[]';
        if (item.chiave === 'email_notifica_produzione') configObj.notifica_produzione = item.valore === 'true';
        if (item.chiave === 'email_attivo') configObj.abilitato = item.valore === 'true';
        if (item.chiave === 'email_abilitato') configObj.abilitato = item.valore === 'true'; // Backup compatibility
      });

      setConfig(configObj);
      setUserConfig({
        destinatari_notifiche: configObj.destinatari_notifiche,
        notifica_produzione: configObj.notifica_produzione,
        abilitato: configObj.abilitato
      });
    } catch (error) {
      console.error('Errore caricamento configurazione email:', error);
      setAlert({ show: true, message: 'Errore nel caricamento della configurazione email', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);

      // Salva solo le configurazioni gestibili dall'utente
      const configs = [
        { chiave: 'email_destinatari_notifiche', valore: userConfig.destinatari_notifiche, descrizione: 'Lista destinatari notifiche' },
        { chiave: 'email_notifica_produzione', valore: userConfig.notifica_produzione.toString(), descrizione: 'Notifiche produzione' },
        { chiave: 'email_attivo', valore: userConfig.abilitato.toString(), descrizione: 'Email abilitate' }
      ];

      await apiService.updateEmailConfig(configs);
      setAlert({ show: true, message: 'Configurazione salvata con successo', type: 'success' });
    } catch (error) {
      console.error('Errore salvataggio configurazione email:', error);
      setAlert({ show: true, message: 'Errore nel salvataggio della configurazione', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      setAlert({ show: true, message: 'Inserisci un indirizzo email per il test', type: 'error' });
      return;
    }

    try {
      setSendingTest(true);
      await apiService.sendTestEmail(testEmail);
      setAlert({ show: true, message: 'Email di test inviata con successo', type: 'success' });
    } catch (error) {
      console.error('Errore invio email di test:', error);
      setAlert({ show: true, message: 'Errore nell\'invio dell\'email di test', type: 'error' });
    } finally {
      setSendingTest(false);
    }
  };

  const handleConfigChange = (field: keyof EmailUserConfig, value: string | boolean) => {
    setUserConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getDestinatarisAsArray = (): string[] => {
    try {
      return userConfig.destinatari_notifiche ? JSON.parse(userConfig.destinatari_notifiche) : [];
    } catch (e) {
      return [];
    }
  };

  const handleAddDestinatario = async () => {
    const email = newDestinatario.trim();
    if (!email || !email.includes('@')) {
      setAlert({ show: true, message: 'Inserisci un indirizzo email valido', type: 'error' });
      return;
    }

    const destinatari = [...getDestinatarisAsArray(), email];
    handleConfigChange('destinatari_notifiche', JSON.stringify(destinatari));
    setNewDestinatario('');

    // Salva automaticamente
    try {
      const configs = [
        { chiave: 'email_destinatari_notifiche', valore: JSON.stringify(destinatari), descrizione: 'Lista destinatari notifiche' }
      ];
      await apiService.updateEmailConfig(configs);
      setAlert({ show: true, message: 'Destinatario aggiunto con successo', type: 'success' });
    } catch (error) {
      console.error('Errore aggiunta destinatario:', error);
      setAlert({ show: true, message: 'Errore nell\'aggiunta del destinatario', type: 'error' });
    }
  };

  const handleRemoveDestinatario = async (index: number) => {
    const destinatari = getDestinatarisAsArray();
    const emailRimossa = destinatari[index];
    destinatari.splice(index, 1);
    handleConfigChange('destinatari_notifiche', JSON.stringify(destinatari));

    // Salva automaticamente
    try {
      const configs = [
        { chiave: 'email_destinatari_notifiche', valore: JSON.stringify(destinatari), descrizione: 'Lista destinatari notifiche' }
      ];
      await apiService.updateEmailConfig(configs);
      setAlert({ show: true, message: `Destinatario ${emailRimossa} rimosso con successo`, type: 'success' });
    } catch (error) {
      console.error('Errore rimozione destinatario:', error);
      setAlert({ show: true, message: 'Errore nella rimozione del destinatario', type: 'error' });
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader title="Configurazione Email" icon={<Mail />} />

      {alert.show && (
        <Alert $variant={alert.type}>
          {alert.message}
        </Alert>
      )}

      <Grid>
        {/* Stato configurazione API e gestione notifiche */}
        <Column>
          <Card>
            <CardHeader>
              <h3>Stato Configurazione API</h3>
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label>API Key Brevo</Label>
                <ReadOnlyBox>
                  {config.brevo_api_key ? (
                    <Badge $variant="success">Configurata</Badge>
                  ) : (
                    <Badge $variant="warning">Non configurata</Badge>
                  )}
                </ReadOnlyBox>
                <HelperText>La chiave API viene gestita dal super amministratore per motivi di sicurezza</HelperText>
              </FormGroup>

              <FormGroup>
                <Label>Email Mittente</Label>
                <ReadOnlyBox>{config.email_mittente || 'Non configurata'}</ReadOnlyBox>
                <HelperText>Email configurata dal super amministratore</HelperText>
              </FormGroup>

              <FormGroup>
                <Label>Nome Mittente</Label>
                <ReadOnlyBox>{config.nome_mittente || 'Non configurato'}</ReadOnlyBox>
                <HelperText>Nome mittente configurato dal super amministratore</HelperText>
              </FormGroup>

              <Alert $variant="info">
                <strong>Nota:</strong> le configurazioni API (chiave API, email mittente e nome mittente)
                vengono gestite dal super amministratore per motivi di sicurezza.
                Puoi gestire solo i destinatari delle notifiche e le impostazioni di invio.
              </Alert>

              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={userConfig.abilitato}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('abilitato', e.target.checked)}
                />
                Abilita invio email
              </CheckboxRow>

              <Divider />

              <DestinatariContainer>
                <Label>Email per le notifiche di produzione</Label>
                {getDestinatarisAsArray().length === 0 ? (
                  <InfoText>Nessun destinatario configurato</InfoText>
                ) : (
                  <DestinatariList>
                    {getDestinatarisAsArray().map((email, index) => (
                      <DestinatarioItem key={index}>
                        <span>{email}</span>
                        <RemoveButton
                          type="button"
                          onClick={() => handleRemoveDestinatario(index)}
                          title="Rimuovi destinatario"
                          aria-label={`Rimuovi ${email}`}
                        >
                          <X size={16} />
                        </RemoveButton>
                      </DestinatarioItem>
                    ))}
                  </DestinatariList>
                )}
                <AddRow>
                  <Input
                    type="email"
                    placeholder="nuovo@destinatario.it"
                    value={newDestinatario}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setNewDestinatario(e.target.value)}
                  />
                  <Button $variant="secondary" type="button" onClick={handleAddDestinatario}>
                    <Plus size={16} />
                    Aggiungi
                  </Button>
                </AddRow>
                <HelperText>
                  Indirizzi email che riceveranno le notifiche quando viene creata una nuova produzione
                </HelperText>
              </DestinatariContainer>

              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={userConfig.notifica_produzione}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('notifica_produzione', e.target.checked)}
                />
                Abilita notifiche automatiche per nuove produzioni
              </CheckboxRow>

              <Button onClick={handleSaveConfig} disabled={saving}>
                <Save size={16} />
                {saving ? 'Salvataggio...' : 'Salva Configurazione'}
              </Button>
            </CardBody>
          </Card>
        </Column>

        {/* Test Email e Informazioni */}
        <Column>
          <Card>
            <CardHeader>
              <h3>Test Email</h3>
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label>Email di Test</Label>
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setTestEmail(e.target.value)}
                  placeholder="test@esempio.com"
                />
                <HelperText>Inserisci un indirizzo email per testare la configurazione</HelperText>
              </FormGroup>

              <Button
                $variant="secondary"
                $fullWidth
                onClick={handleSendTest}
                disabled={sendingTest || !userConfig.abilitato || !config.brevo_api_key}
              >
                {sendingTest ? <TestTube size={16} /> : <Send size={16} />}
                {sendingTest ? 'Invio...' : 'Invia Test'}
              </Button>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h3>Informazioni Sistema Email</h3>
            </CardHeader>
            <CardBody>
              <InfoText>
                Le email vengono inviate automaticamente ai destinatari configurati quando viene creata una nuova produzione.
              </InfoText>

              <InfoText>
                <strong>Stato sistema:</strong>{' '}
                {config.brevo_api_key && config.email_mittente ?
                  'Configurato correttamente' :
                  'Configurazione incompleta (contattare il super admin)'
                }
              </InfoText>

              <InfoText>
                <strong>Destinatari configurati:</strong> {getDestinatarisAsArray().length}
              </InfoText>

              <InfoText>
                <strong>Notifiche abilitate:</strong> {userConfig.abilitato ? 'Sì' : 'No'}
              </InfoText>

              <Divider />

              <InfoText>
                <strong>Per la configurazione API:</strong><br />
                Le impostazioni sensibili (API Key Brevo, email mittente, nome mittente)
                sono gestite dal super amministratore per motivi di sicurezza.<br /><br />

                Come utente puoi:<br />
                • Gestire la lista dei destinatari delle notifiche<br />
                • Abilitare/disabilitare l'invio di email<br />
                • Testare l'invio di email di prova<br />
                • Configurare le notifiche automatiche per le produzioni
              </InfoText>
            </CardBody>
          </Card>
        </Column>
      </Grid>
    </PageContainer>
  );
};

export default ImpostazioniEmailPage;
